import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

import { Button, IconButton } from 'components/Button';
import { CaretDownIcon, CaretUpIcon, XIcon } from 'components/Icon';
import { Popover, PopoverContent, PopoverTrigger } from 'components/Popover';
import { splitButtonRecipe } from 'components/SplitButton/SplitButton.recipe';
import { Tooltip } from 'components/Tooltip';
import { cn } from 'components/utils';

import {
  filterClearRecipe,
  filterPanelSurfaceRecipe,
  filterTriggerLabelRecipe,
  filterTriggerRecipe,
  filterTriggerValueRecipe,
} from './FilterButton.recipe';
import { FilterPanel } from './FilterPanel';
import { formatFilterSummary, SUMMARY_LABEL_LIMIT } from './formatFilterSummary';

import type { FilterButtonProps } from './types';

/**
 * Design System 2.0 filter button: the trigger for ONE filter, and the panel where its choices are
 * made.
 *
 * Designed in Figma as `Filter button`, `Filter panel` and `Filter bar` (file
 * 5aSkE0U4TeWbuIZDvccHjc, page `Filter bar [Draft]`). Filter bar is `FilterBar`; the first two are
 * this component, because a trigger with no panel is not a control and a panel with no trigger has
 * nothing to open from.
 *
 * ```tsx
 * <FilterButton
 *   selectionMode="multiple"
 *   label={t('role')}
 *   labels={labels}
 *   options={roles}
 *   value={roleIds}
 *   onValueChange={setRoleIds}
 * />
 * ```
 *
 * When something is picked the trigger shows it after the category ("Role: Admin, Editor +1 more")
 * and a separate Clear cell appears beside it, the Split button's shape: two buttons, each with its
 * own focus ring and name, never one nested inside the other.
 *
 * TWO MODES, `applyMode`. `apply` (default) keeps a draft: nothing reaches `onValueChange` until
 * Apply, and Escape, a click outside or a closed panel throws the draft away. `live` has no draft
 * and no Apply: each pick reports at once, for filters where a change is instant and cheap. A
 * filter behind the anonymity guard (`validate`) is an `apply` filter.
 *
 * Clear empties the filter, reports it and closes the panel, in both modes.
 *
 * `children` replace the option rows for a caller that needs its own content in the panel; the
 * footer stays.
 *
 * The panel is a `Popover`, so it is `role="dialog"` named by `labels.panel`, focus moves in on
 * open and returns to the trigger on close. Use `Select` or `MultiSelect` for a value in a form,
 * which commits each pick and lives in a field; this is for narrowing a list already on screen.
 */
export const FilterButton = forwardRef<HTMLButtonElement, FilterButtonProps>((props, forwardedRef) => {
  const {
    label,
    labels,
    options,
    optionsStatus = 'ready',
    onRetry,
    searchable = false,
    size = 'regular',
    applyMode = 'apply',
    disabled,
    side = 'bottom',
    align = 'start',
    className,
    panelClassName,
    children,
    open: openProp,
    defaultOpen = false,
    onOpenChange,
    // The rest is the trigger's: `aria-*`, `data-*`, `id`, handlers.
    selectionMode,
    value,
    onValueChange,
    validate,
    ...rest
  } = props;

  const multiple = selectionMode === 'multiple';

  // One shape internally: a list. A single pick is a list of one, and goes back out as `string | null`.
  const applied = useMemo<string[]>(() => {
    if (selectionMode === 'multiple') return value;
    return value === null ? [] : [value];
  }, [selectionMode, value]);
  const appliedKey = applied.join('\u0000');

  const commit = useCallback(
    (next: string[]) => {
      if (selectionMode === 'multiple') onValueChange(next);
      else onValueChange(next[0] ?? null);
    },
    [selectionMode, onValueChange]
  );

  // Open state follows Popover: controllable, and uncontrolled by default.
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const isOpen = isControlled ? openProp : uncontrolledOpen;

  const [draft, setDraft] = useState<string[]>(applied);
  const lastCommitted = useRef(appliedKey);

  const setOpen = useCallback(
    (next: boolean) => {
      // The draft starts from what is applied every time the panel opens, which is what makes
      // Escape and a click outside discard: there is nothing to undo, only a copy to replace.
      if (next) setDraft(applied);
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [applied, isControlled, onOpenChange]
  );

  // A change we did not make (Back, Forward, Clear all in a bar, a label chip) closes an open
  // `apply` panel and drops its draft, so nobody applies a draft that was built on stale ground.
  // `live` panels stay open: their own picks are what change the value.
  const previousKey = useRef(appliedKey);
  useEffect(() => {
    if (previousKey.current === appliedKey) return;
    previousKey.current = appliedKey;
    const external = appliedKey !== lastCommitted.current;
    // Whatever is applied now is the last thing anyone committed, ours or not. Left as it was, a later
    // external change back to that old value would read as our own and leave a stale draft open.
    lastCommitted.current = appliedKey;
    if (isOpen && applyMode === 'apply' && external) setOpen(false);
  }, [appliedKey, applyMode, isOpen, setOpen]);

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const setTriggerRef = useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) (forwardedRef as MutableRefObject<HTMLButtonElement | null>).current = node;
    },
    [forwardedRef]
  );

  const labelOf = useMemo(() => new Map(options.map((option) => [option.value, option.label])), [options]);
  // A value with no matching option is shown as itself, as `TagInput` does, so a stale URL still says something.
  const pickedLabels = applied.map((value) => labelOf.get(value) ?? value);
  const filled = applied.length > 0;
  const summary = formatFilterSummary(pickedLabels, labels.more);

  const validation =
    selectionMode === 'multiple' && applyMode === 'apply' && validate ? validate(draft, options) : undefined;

  const clearAll = () => {
    lastCommitted.current = '';
    commit([]);
  };

  const [tooltipOpen, setTooltipOpen] = useState(false);
  // Only worth showing once the trigger has had to summarise; below that it would repeat itself.
  const showTooltip = pickedLabels.length > SUMMARY_LABEL_LIMIT && !isOpen;

  return (
    <div
      className={cn(filled ? splitButtonRecipe({ size }) : 'inline-flex', 'max-w-full', className)}
      data-part="filter-button"
    >
      <Popover open={isOpen} onOpenChange={setOpen}>
        <Tooltip
          label={pickedLabels.join(', ') || label}
          open={showTooltip && tooltipOpen}
          side="top"
          onOpenChange={setTooltipOpen}
        >
          <PopoverTrigger
            render={
              <Button
                ref={setTriggerRef}
                appearance="secondary"
                aria-label={filled ? (labels.summaryName?.(label, pickedLabels) ?? `${label}: ${summary}`) : undefined}
                className={filterTriggerRecipe({ filled })}
                disabled={disabled}
                endIcon={isOpen ? <CaretUpIcon /> : <CaretDownIcon />}
                size={size}
                {...rest}
              >
                <span className="inline-flex min-w-0 items-baseline gap-1">
                  <span className={filled ? filterTriggerLabelRecipe() : undefined} data-part="label">
                    {filled ? `${label}:` : label}
                  </span>
                  {filled && (
                    <span className={filterTriggerValueRecipe()} data-part="value">
                      {summary}
                    </span>
                  )}
                </span>
              </Button>
            }
          />
        </Tooltip>

        <PopoverContent
          align={align}
          aria-label={labels.panel}
          className={cn(filterPanelSurfaceRecipe(), panelClassName)}
          side={side}
        >
          <FilterPanel
            applyMode={applyMode}
            labels={labels}
            mode={multiple ? 'multiple' : 'single'}
            options={options}
            searchable={searchable}
            selected={applyMode === 'live' ? applied : draft}
            status={optionsStatus}
            validation={validation}
            onApply={() => {
              lastCommitted.current = draft.join('\u0000');
              commit(draft);
              setOpen(false);
            }}
            onClear={() => {
              clearAll();
              setOpen(false);
            }}
            onRetry={onRetry}
            onSelectedChange={(next) => {
              if (applyMode === 'live') {
                lastCommitted.current = next.join('\u0000');
                commit(next);
              } else {
                setDraft(next);
              }
            }}
          >
            {children}
          </FilterPanel>
        </PopoverContent>
      </Popover>

      {filled && (
        <IconButton
          appearance="secondary"
          aria-label={labels.clear(label)}
          className={filterClearRecipe()}
          disabled={disabled}
          size={size}
          onClick={() => {
            clearAll();
            triggerRef.current?.focus();
          }}
        >
          <XIcon />
        </IconButton>
      )}
    </div>
  );
});
